import { Button } from '@/components/ui/button'
import Page from '@/components/ui/Page'
import { Dispatch, SetStateAction, useState } from 'react'
import Animation from './Tabs/Animation'
import ThreeD from './Tabs/ThreeD'
import TwoD from './Tabs/TwoD'
import EldritchGame from './Tabs/EldritchGame'
import { twMerge } from 'tailwind-merge'
import FairyRing from './Tabs/FairyRing'
import RussianVillage from './Tabs/RussianVillage'
import TrainProject from './Tabs/TrainProject'
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  useSidebar,
} from '@/components/ui/sidebar'
import { PanelLeft } from 'lucide-react'
import { useIsMobile } from '@/hooks/use-mobile'

type Tabs =
  | '3D'
  | 'Animation'
  | '2D'
  | 'Eldritch Game'
  | 'Fairy Ring'
  | 'Russian Village'
  | 'Train Project'

const SubTab = ({ tab }: { tab: Tabs }) => {
  switch (tab) {
    case 'Animation':
      return <Animation />
    case '2D':
      return <TwoD />
    case 'Eldritch Game':
      return <EldritchGame />
    case 'Fairy Ring':
      return <FairyRing />
    case 'Russian Village':
      return <RussianVillage />
    case 'Train Project':
      return <TrainProject />
    case '3D':
    default:
      return <ThreeD />
  }
}

interface MenuItem {
  id: Tabs
  label: string
}

const menuItems: MenuItem[] = [
  { id: 'Eldritch Game', label: 'Eldritch Game' },
  { id: 'Fairy Ring', label: 'Fairy Doorway' },
  { id: '3D', label: '3D' },
  { id: '2D', label: '2D' },
  { id: 'Animation', label: 'Animation' },
  { id: 'Train Project', label: 'A Final Eden' },
  { id: 'Russian Village', label: 'Medieval Russian' },
]

const PortfolioSideBar = ({
  tab,
  setTab,
}: { tab: Tabs; setTab: Dispatch<SetStateAction<Tabs>> }) => {
  const isMobile = useIsMobile()
  const { toggleSidebar } = useSidebar()
  return (
    <Sidebar className="z-0 mt-16 pt-4 bg-[#BCB8B1] ">
      <SidebarContent className="bg-[#BCB8B1]">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((menuItem) => (
                <SidebarMenuItem className="mb-4" key={menuItem.id}>
                  <SidebarMenuButton
                    className={twMerge(
                      'cursor-pointer h-full transition-colors duration-200',
                      tab === menuItem.id ? 'bg-white' : undefined,
                    )}
                    onClick={() => {
                      setTab(menuItem.id)
                      if (isMobile) {
                        toggleSidebar()
                      }
                    }}
                  >
                    <p className="text-2xl w-full text-center font-bold">
                      {menuItem.label}
                    </p>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  )
}

const SidebarButton = () => {
  const { toggleSidebar } = useSidebar()

  return (
    <Button
      className="fixed rounded-full w-fit h-fit px-0 p-4 bottom-0 right-0 m-4 md:right-auto md:bottom-auto z-50 shadow-lg/30 hover:bg-white hover:text-black transition-colors duration-500"
      onClick={toggleSidebar}
    >
      <PanelLeft className="size-8 md:size-16" />
    </Button>
  )
}

const Portfolio = () => {
  const [tab, setTab] = useState<Tabs>('Eldritch Game')
  return (
    <Page className="p-0 max-w-screen relative">
      <SidebarProvider defaultOpen>
        <PortfolioSideBar tab={tab} setTab={setTab} />
        <div className="relative w-full max-w-screen overflow-x-hidden overflow-y-auto max-h-[calc(100vh-64px)]">
          <SidebarButton />
          <SubTab tab={tab} />
        </div>
      </SidebarProvider>
    </Page>
  )
}

export default Portfolio
